import React, { useState, useEffect } from 'react';
import {
    CheckCircle,
    Clock,
    AlertCircle,
    FileText,
    TrendingUp,
    Users,
    DollarSign,
    Calendar,
    Filter,
    Download,
    RefreshCw,
    Eye,
    Check,
    X,
    Receipt,
    ShoppingBag,
    CreditCard,
    Building,
    Package,
    User,
    Search,
    ChevronDown,
    ChevronUp,
    RotateCcw,
    Brain,
    ExternalLink,
    CheckSquare,
    Square,
    Trash2
} from 'lucide-react';

const ApproverDashboard = ({ user, currentOrganization }) => {
    const [activeTab, setActiveTab] = useState('daily-spends');
    const [componentData, setComponentData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('pending');
    const [searchTerm, setSearchTerm] = useState('');
    const [stats, setStats] = useState({
        pending: 0,
        approved: 0,
        rejected: 0,
        totalAmount: 0
    });

    // Bulk selection states
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [bulkProcessing, setBulkProcessing] = useState(false);

    // Modal states
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [itemDetails, setItemDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Get data from session storage if props are not available
    const userData = user || JSON.parse(sessionStorage.getItem('userData') || '{}');
    const userCompanies = JSON.parse(sessionStorage.getItem('userCompanies') || '[]');

    const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api/approvals';

    // Navigation tabs for approver using CORRECT approver API endpoints
    const approverTabs = [
        {
            id: 'daily-spends',
            name: 'Daily Spend',
            icon: Brain,
            endpoint: '/daily-spends',
            type: 'daily_spend'
        },
        {
            id: 'expenses',
            name: 'Expenses',
            icon: Receipt,
            endpoint: '/expenses',
            type: 'expense'
        },
        {
            id: 'reimbursements',
            name: 'Reimbursements',
            icon: FileText,
            endpoint: '/reimbursements',
            type: 'reimbursement'
        },
        {
            id: 'purchases',
            name: 'Purchases',
            icon: ShoppingBag,
            endpoint: '/purchases/with-expense-data',
            type: 'purchase'
        },
        {
            id: 'vendor-credits',
            name: 'Vendor Credits',
            icon: CreditCard,
            endpoint: '/debit-notes',
            type: 'debit_note'
        },
        {
            id: 'recurring-expenses',
            name: 'Recurring Expenses',
            icon: RotateCcw,
            endpoint: '/recurring-expenses',
            type: 'recurring_expense'
        },
        {
            id: 'invoices',
            name: 'Sales/Invoices',
            icon: TrendingUp,
            endpoint: '/sales-data',
            type: 'sales_data'
        },
        {
            id: 'credit-notes',
            name: 'Credit Notes',
            icon: FileText,
            endpoint: '/credit-notes',
            type: 'credit_note'
        },
        {
            id: 'recurring-invoices',
            name: 'Recurring Invoices',
            icon: RotateCcw,
            endpoint: '/recurring-invoices',
            type: 'recurring_invoice'
        }
    ];

    useEffect(() => {
        if (userData?.companyId || userCompanies?.[0]?.companyId) {
            fetchComponentData();
            fetchApprovalStats();
        }
    }, [activeTab, userData?.companyId]);

    // Clear selections when tab changes
    useEffect(() => {
        setSelectedItems([]);
        setSelectAll(false);
    }, [activeTab, filter]);

    const fetchApprovalStats = async () => {
        const companyId = user?.companyId || userCompanies?.[0]?.companyId;

        if (!companyId) {
            console.error('No company ID available for approver');
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/stats?company_id=${companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setStats(data.stats);
                }
            }
        } catch (error) {
            console.error('Error fetching approval stats:', error);
        }
    };

    const fetchComponentData = async () => {
        const companyId = userData?.companyId || userCompanies?.[0]?.companyId;

        if (!companyId) {
            console.error('No company ID available for approver');
            setComponentData([]);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const currentTabConfig = approverTabs.find(tab => tab.id === activeTab);

            if (!currentTabConfig) {
                setComponentData([]);
                setLoading(false);
                return;
            }

            console.log(`Fetching data for ${activeTab} with company_id: ${companyId}`);

            const response = await fetch(`${API_BASE_URL}${currentTabConfig.endpoint}?company_id=${companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    const items = data.data || [];

                    const processedItems = items.map(item => ({
                        ...item,
                        approver_status: item.approver_status || 'pending_approval',
                        approved_by: item.approved_by || null,
                        approved_date: item.approved_date || null,
                        rejection_reason: item.rejection_reason || null
                    }));

                    setComponentData(Array.isArray(processedItems) ? processedItems : []);
                    calculateStats(processedItems);
                } else {
                    console.error('API returned error:', data.message);
                    setComponentData([]);
                }
            } else {
                console.error('API request failed:', response.status);
                setComponentData([]);
            }
        } catch (error) {
            console.error('Error fetching component data:', error);
            setComponentData([]);
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (data) => {
        const pending = data.filter(item => !item.approver_status || item.approver_status === 'pending_approval').length;
        const approved = data.filter(item => item.approver_status === 'approved').length;
        const rejected = data.filter(item => item.approver_status === 'rejected').length;
        const totalAmount = data.reduce((sum, item) => sum + (parseFloat(item.amount) || parseFloat(item.total) || 0), 0);
    };

    const fetchItemDetails = async (item) => {
        setLoadingDetails(true);
        try {
            const token = localStorage.getItem('authToken');
            const currentTabConfig = approverTabs.find(tab => tab.id === activeTab);
            const companyId = userData?.companyId || userCompanies?.[0]?.companyId;

            const response = await fetch(`${API_BASE_URL}/item-details?itemId=${item.id}&itemType=${currentTabConfig.type}&company_id=${companyId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setItemDetails(data.data);
                    setSelectedItem(item);
                    setShowDetailsModal(true);
                }
            }
        } catch (error) {
            console.error('Error fetching item details:', error);
            alert('Failed to fetch item details');
        } finally {
            setLoadingDetails(false);
        }
    };

    // Enhanced Item Details Modal with Invoice Link
    const ItemDetailsModal = () => {
        if (!showDetailsModal || !itemDetails) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900">Item Details</h2>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Item Information */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-3">Item Information</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">ID:</span>
                                        <span className="ml-2 font-medium">#{itemDetails.id}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Amount:</span>
                                        <span className="ml-2 font-medium">{formatCurrency(itemDetails.amount || itemDetails.total)}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Date:</span>
                                        <span className="ml-2 font-medium">{new Date(itemDetails.created_at || itemDetails.date).toLocaleDateString()}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Status:</span>
                                        <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusColor(itemDetails.approver_status || 'pending_approval')}`}>
                                            {itemDetails.approver_status || 'Pending'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Invoice Link Section */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-3">Invoice Document</h3>
                                {itemDetails.aws_link ? (
                                    <div className="flex items-center gap-2">
                                        <ExternalLink className="w-4 h-4 text-blue-600" />
                                        <a
                                            href={itemDetails.aws_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 font-medium underline"
                                        >
                                            View Invoice
                                        </a>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-gray-500">
                                        <FileText className="w-4 h-4" />
                                        <span>No Invoice Available</span>
                                    </div>
                                )}
                            </div>

                            {/* Submitter Information */}
                            <div className="bg-green-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-3">Submitted By</h3>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Name:</span>
                                        <span className="ml-2 font-medium">{itemDetails.submitted_by_name || 'Unknown'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Role:</span>
                                        <span className="ml-2 font-medium capitalize">{itemDetails.submitted_by_role || 'Unknown'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">WhatsApp:</span>
                                        <span className="ml-2 font-medium">{itemDetails.submitted_by_whatsapp || 'Not provided'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Email:</span>
                                        <span className="ml-2 font-medium">{itemDetails.submitted_by_email || 'Not provided'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Description/Details */}
                            {(itemDetails.description || itemDetails.nature_of_expenses || itemDetails.items) && (
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
                                    <p className="text-sm text-gray-700">
                                        {itemDetails.description || itemDetails.nature_of_expenses || itemDetails.items || 'No description available'}
                                    </p>
                                </div>
                            )}

                            {/* Vendor/Customer Information */}
                            {(itemDetails.vendor_name || itemDetails.customer_name) && (
                                <div className="bg-orange-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-3">
                                        {itemDetails.vendor_name ? 'Vendor' : 'Customer'} Information
                                    </h3>
                                    <div className="text-sm">
                                        <span className="text-gray-600">Name:</span>
                                        <span className="ml-2 font-medium">{itemDetails.vendor_name || itemDetails.customer_name}</span>
                                    </div>
                                </div>
                            )}

                            {/* Approval Information */}
                            {itemDetails.approver_status && itemDetails.approver_status !== 'pending_approval' && (
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-3">Approval Information</h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        {itemDetails.approved_by && (
                                            <div>
                                                <span className="text-gray-600">Processed By:</span>
                                                <span className="ml-2 font-medium">{itemDetails.approved_by}</span>
                                            </div>
                                        )}
                                        {itemDetails.approved_date && (
                                            <div>
                                                <span className="text-gray-600">Processed Date:</span>
                                                <span className="ml-2 font-medium">{new Date(itemDetails.approved_date).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        {itemDetails.rejection_reason && (
                                            <div className="col-span-2">
                                                <span className="text-gray-600">Rejection Reason:</span>
                                                <span className="ml-2 font-medium text-red-600">{itemDetails.rejection_reason}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Bulk selection handlers
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedItems([]);
        } else {
            const pendingItems = filteredData.filter(item =>
                (!item.approver_status || item.approver_status === 'pending_approval')
            );
            setSelectedItems(pendingItems.map(item => item.id));
        }
        setSelectAll(!selectAll);
    };

    const handleSelectItem = (itemId) => {
        setSelectedItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    // Single approval handler
    const handleApproval = async (itemId, action, rejectionReason = null) => {
        try {
            const token = localStorage.getItem('authToken');
            const currentTabConfig = approverTabs.find(tab => tab.id === activeTab);
            const companyId = userData?.companyId || userCompanies?.[0]?.companyId;
            const approverName = userData?.fullName || userData?.username || user?.fullName || user?.username || 'Unknown Approver';

            if (!companyId) {
                alert('Error: No company ID available');
                return;
            }

            if (!approverName || approverName === 'Unknown Approver') {
                alert('Error: Unable to identify approver. Please refresh and try again.');
                return;
            }

            const response = await fetch(`${API_BASE_URL}/action`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    itemId,
                    itemType: currentTabConfig.type,
                    action,
                    approver_name: approverName,
                    company_id: companyId,
                    rejection_reason: rejectionReason
                })
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setComponentData(prev => prev.map(item =>
                        item.id === itemId
                            ? {
                                ...item,
                                approver_status: action === 'approve' ? 'approved' : 'rejected',
                                approved_by: approverName,
                                approved_date: new Date().toISOString(),
                                rejection_reason: rejectionReason
                            }
                            : item
                    ));

                    fetchApprovalStats();

                    if (showDetailsModal && selectedItem?.id === itemId) {
                        setTimeout(() => fetchItemDetails(selectedItem), 500);
                    }
                } else {
                    alert('Error: ' + (result.message || 'Unknown error occurred'));
                }
            } else {
                alert(`Failed to process approval (HTTP ${response.status})`);
            }
        } catch (error) {
            console.error('Error processing approval:', error);
            alert('Error processing approval. Please try again.');
        }
    };

    // Bulk approval handler
    const handleBulkApproval = async (action) => {
        if (selectedItems.length === 0) {
            alert('Please select items to process');
            return;
        }

        let rejectionReason = null;
        if (action === 'reject') {
            rejectionReason = prompt('Enter rejection reason for all selected items (optional):');
        }

        setBulkProcessing(true);

        try {
            const token = localStorage.getItem('authToken');
            const currentTabConfig = approverTabs.find(tab => tab.id === activeTab);
            const companyId = userData?.companyId || userCompanies?.[0]?.companyId;
            const approverName = userData?.fullName || userData?.username || user?.fullName || user?.username || 'Unknown Approver';

            if (!companyId) {
                alert('Error: No company ID available');
                return;
            }

            if (!approverName || approverName === 'Unknown Approver') {
                alert('Error: Unable to identify approver. Please refresh and try again.');
                return;
            }

            console.log('Processing bulk approval:', {
                itemIds: selectedItems,
                action,
                approverName,
                companyId,
                itemType: currentTabConfig.type,
                rejectionReason
            });

            // Call the new bulk-action endpoint
            const response = await fetch(`${API_BASE_URL}/bulk-action`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    itemIds: selectedItems,
                    itemType: currentTabConfig.type,
                    action,
                    approver_name: approverName,
                    company_id: companyId,
                    rejection_reason: rejectionReason
                })
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    const { results, summary } = result.data;

                    console.log('Bulk approval result:', result);

                    // Update local state for successful items
                    if (results.successful.length > 0) {
                        setComponentData(prev => prev.map(item => {
                            const successfulItem = results.successful.find(s => s.itemId === item.id);
                            if (successfulItem) {
                                return {
                                    ...item,
                                    approver_status: action === 'approve' ? 'approved' : 'rejected',
                                    approved_by: approverName,
                                    approved_date: successfulItem.approved_date,
                                    rejection_reason: rejectionReason
                                };
                            }
                            return item;
                        }));
                    }

                    // Show detailed results
                    let message = `Bulk ${action} completed!\n`;
                    message += `✅ Successful: ${summary.successful}\n`;
                    if (summary.failed > 0) {
                        message += `❌ Failed: ${summary.failed}\n`;
                        message += `\nFailed items:\n`;
                        results.failed.forEach(failure => {
                            message += `- Item #${failure.itemId}: ${failure.reason}\n`;
                        });
                    }

                    alert(message);

                    // Clear selections and refresh stats
                    setSelectedItems([]);
                    setSelectAll(false);
                    fetchApprovalStats();

                } else {
                    console.error('Bulk API Error:', result);
                    alert('Error: ' + (result.message || 'Unknown error occurred'));
                }
            } else {
                const errorText = await response.text();
                console.error('Bulk HTTP Error:', response.status, errorText);
                alert(`Failed to process bulk approval (HTTP ${response.status})`);
            }

        } catch (error) {
            console.error('Bulk approval error:', error);
            alert('Error during bulk processing. Please try again.');
        } finally {
            setBulkProcessing(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending_approval': return 'text-yellow-600 bg-yellow-100';
            case 'approved': return 'text-green-600 bg-green-100';
            case 'rejected': return 'text-red-600 bg-red-100';
            default: return 'text-yellow-600 bg-yellow-100';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const filteredData = componentData.filter(item => {
        const matchesSearch = searchTerm === '' ||
            item.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.item?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());

        const itemStatus = item.approver_status || 'pending_approval';
        const matchesFilter = filter === 'all' ||
            (filter === 'pending' && itemStatus === 'pending_approval') ||
            (filter === 'approved' && itemStatus === 'approved') ||
            (filter === 'rejected' && itemStatus === 'rejected');

        return matchesSearch && matchesFilter;
    });

    const pendingItems = filteredData.filter(item =>
        (!item.approver_status || item.approver_status === 'pending_approval')
    );

    const ApprovalCard = ({ item }) => {
        const itemStatus = item.approver_status || 'pending_approval';
        const isPending = itemStatus === 'pending_approval';
        const isApproved = itemStatus === 'approved';
        const isRejected = itemStatus === 'rejected';
        const isSelected = selectedItems.includes(item.id);

        return (
            <div className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                        {isPending && (
                            <button
                                onClick={() => handleSelectItem(item.id)}
                                className="text-blue-600 hover:text-blue-800"
                            >
                                {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                            </button>
                        )}
                        <Receipt className="w-4 h-4 text-gray-500" />
                        <h3 className="font-medium text-gray-900">
                            {item.invoice_no || item.reference_number || `#${item.id}`}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(itemStatus)}`}>
                            {isPending ? 'Pending' : isApproved ? 'Approved' : 'Rejected'}
                        </span>
                    </div>
                    <div className="text-right">
                        <p className="font-semibold text-gray-900">
                            {formatCurrency(item.amount || item.total)}
                        </p>
                        <p className="text-xs text-gray-500">
                            {new Date(item.created_at || item.date).toLocaleDateString()}
                        </p>
                    </div>
                </div>

                <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Description:</span>
                        <span className="font-medium truncate ml-2">
                            {item.description || item.item || 'No description'}
                        </span>
                    </div>

                    {item.vendor_name && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Vendor:</span>
                            <span className="font-medium">{item.vendor_name}</span>
                        </div>
                    )}

                    {item.customer_name && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Customer:</span>
                            <span className="font-medium">{item.customer_name}</span>
                        </div>
                    )}

                    {item.employee_name && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Employee:</span>
                            <span className="font-medium">{item.employee_name}</span>
                        </div>
                    )}

                    {isApproved && item.approved_by && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Approved by:</span>
                            <span className="font-medium text-green-600">{item.approved_by}</span>
                        </div>
                    )}

                    {isRejected && item.rejection_reason && (
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Rejection Reason:</span>
                            <span className="font-medium text-red-600">{item.rejection_reason}</span>
                        </div>
                    )}
                </div>

                {/* Approval Actions */}
                <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                        {isPending ? (
                            <>
                                <button
                                    onClick={() => handleApproval(item.id, 'approve')}
                                    className="flex items-center gap-1 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                                >
                                    <Check className="w-3 h-3" />
                                    Approve
                                </button>

                                <button
                                    onClick={() => {
                                        const reason = prompt('Enter rejection reason (optional):');
                                        handleApproval(item.id, 'reject', reason);
                                    }}
                                    className="flex items-center gap-1 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                                >
                                    <X className="w-3 h-3" />
                                    Reject
                                </button>
                            </>
                        ) : (
                            <div className={`flex items-center gap-2 ${isApproved ? 'text-green-600' : 'text-red-600'}`}>
                                {isApproved ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                <span className="text-sm font-medium">
                                    {isApproved ? 'Approved' : 'Rejected'}
                                </span>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => fetchItemDetails(item)}
                        disabled={loadingDetails}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm disabled:opacity-50"
                        title="View detailed information"
                    >
                        <Eye className="w-4 h-4" />
                        {loadingDetails ? 'Loading...' : 'View Details'}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Approver Dashboard</h1>
                        <p className="text-gray-600">Review and approve company transactions - Read-only access with approval controls only</p>
                        <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                            <span>Company: <strong>{userData?.companyName || userCompanies?.[0]?.companyName}</strong></span>
                            <span>•</span>
                            <span>Access: <strong>Approver (Read-Only)</strong></span>
                            <span>•</span>
                            <span>User: <strong>{userData?.fullName}</strong></span>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            fetchComponentData();
                            fetchApprovalStats();
                        }}
                        className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition-colors"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh Stats
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Pending Approval</p>
                            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                        </div>
                        <Clock className="w-8 h-8 text-yellow-500" />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Approved</p>
                            <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Rejected</p>
                            <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                        </div>
                        <X className="w-8 h-8 text-red-500" />
                    </div>
                </div>

                {/* <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Amount</p>
                            <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.totalAmount)}</p>
                        </div>
                        <DollarSign className="w-8 h-8 text-purple-500" />
                    </div>
                </div> */}
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white border border-gray-200 rounded-lg mb-6">
                <div className="border-b border-gray-200">
                    <nav className="flex overflow-x-auto">
                        {approverTabs.map((tab) => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === tab.id
                                        ? 'border-teal-500 text-teal-600 bg-teal-50'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    <IconComponent className="w-4 h-4" />
                                    {tab.name}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                <div className="p-4 bg-red-50 border-l-4 border-red-400">
                    <div className="flex items-center gap-2 text-red-800">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                            🚫 APPROVER MODE: All editing, syncing, and creation functions are disabled. You can only approve/reject existing items.
                        </span>
                    </div>
                </div>
            </div>

            {/* Bulk Actions Bar */}
            {pendingItems.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSelectAll}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                                >
                                    {selectAll ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                    <span className="text-sm font-medium">
                                        Select All Pending ({pendingItems.length})
                                    </span>
                                </button>
                            </div>

                            {selectedItems.length > 0 && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <span>{selectedItems.length} selected</span>
                                </div>
                            )}
                        </div>

                        {selectedItems.length > 0 && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => handleBulkApproval('approve')}
                                    disabled={bulkProcessing}
                                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                                >
                                    {bulkProcessing ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Check className="w-4 h-4" />
                                    )}
                                    Bulk Approve ({selectedItems.length})
                                </button>

                                <button
                                    onClick={() => handleBulkApproval('reject')}
                                    disabled={bulkProcessing}
                                    className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                                >
                                    {bulkProcessing ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <X className="w-4 h-4" />
                                    )}
                                    Bulk Reject ({selectedItems.length})
                                </button>

                                <button
                                    onClick={() => {
                                        setSelectedItems([]);
                                        setSelectAll(false);
                                    }}
                                    className="flex items-center gap-1 text-gray-600 hover:text-gray-800 px-2 py-1"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">Approval Status:</span>
                        </div>

                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                        >
                            <option value="all">All Items</option>
                            <option value="pending">Pending Approval</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search items..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm w-64"
                        />
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-4 gap-4 text-sm">
                    <div className="bg-yellow-50 p-2 rounded">
                        <span className="text-yellow-600 font-medium">Pending: </span>
                        <span className="font-bold">{filteredData.filter(item => !item.approver_status || item.approver_status === 'pending_approval').length}</span>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                        <span className="text-green-600 font-medium">Approved: </span>
                        <span className="font-bold">{filteredData.filter(item => item.approver_status === 'approved').length}</span>
                    </div>
                    <div className="bg-red-50 p-2 rounded">
                        <span className="text-red-600 font-medium">Rejected: </span>
                        <span className="font-bold">{filteredData.filter(item => item.approver_status === 'rejected').length}</span>
                    </div>
                    <div className="bg-blue-50 p-2 rounded">
                        <span className="text-blue-600 font-medium">Total: </span>
                        <span className="font-bold">{filteredData.length}</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                    </div>
                ) : filteredData.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredData.map((item) => (
                            <ApprovalCard key={item.id} item={item} />
                        ))}
                    </div>
                ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                        <h3 className="font-medium text-gray-900 mb-1">No items found</h3>
                        <p className="text-gray-600 text-sm">
                            {filter === 'pending'
                                ? 'No pending approvals at the moment.'
                                : 'No items match your current filter criteria.'
                            }
                        </p>
                    </div>
                )}
            </div>

            {/* Item Details Modal */}
            <ItemDetailsModal />
        </div>
    );
};

export default ApproverDashboard;